import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useEvaluation } from "../../../../hooks/useEvaluations";
import { getEvaluationByHostId } from "../../../../services/evaluationService";
import SkeletonLoader from "../../../../components/SkeletonLoader";
import boxIcon from "../../../../assets/box.svg";
import { getEvaluationShortLabel } from "../../../../constants/evaluationLabels";

const SCORE_MIN = 1;
const SCORE_MAX = 5;
const SCORE_TRACK_WIDTH = 210;

const clampScore = (value) => {
  const numericValue = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(numericValue)) return 0;
  return Math.min(Math.max(numericValue, 0), SCORE_MAX);
};

const getScoreColor = (value) => {
  if (value <= 2.33) return "#dc2626";
  if (value <= 3.66) return "#eab308";
  return "#16a34a";
};

export function ScoreRuler({ value = 0 }) {
  const safeValue = clampScore(value);
  const normalizedValue = safeValue <= SCORE_MIN
    ? 0
    : ((safeValue - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100;
  const markerPercent = safeValue <= 0 ? 0 : normalizedValue;
  const scoreColor = getScoreColor(safeValue);

  return (
    <ScoreRulerWrap>
      <ScoreValue $color={scoreColor}>{safeValue.toFixed(2)}</ScoreValue>
      <ScoreTrack>
        <ScoreTrackBase />
        <ScoreTrackOverlay $width={100 - markerPercent} />
        <ScoreMarker $left={markerPercent} $color={scoreColor} />
        <ScaleTicks>
          {[1, 2, 3, 4, 5].map((tick) => (
            <ScaleTick key={tick} $left={((tick - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100} />
          ))}
        </ScaleTicks>
      </ScoreTrack>
      <ScaleLabels>
        {[1, 2, 3, 4, 5].map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </ScaleLabels>
    </ScoreRulerWrap>
  );
}

export default function UnifiedEvaluationsTable({
  selectedType,
  selectedHostId,
  restoName,
  restoId,
  disableRestoFetch = false
}) {
  const { getEvalByResto, restoEvaluation, getEvalRestoById, loading, error } = useEvaluation();
  const [hostEvaluations, setHostEvaluations] = useState([]);
  const [hostLoading, setHostLoading] = useState(false);
  const [hostError, setHostError] = useState(null);

  // Cargar evaluaciones del restaurante
  useEffect(() => {
    if (selectedType === "restaurant" && !disableRestoFetch) {
      const load = restoId ? getEvalRestoById(restoId) : getEvalByResto();
      load.catch(err => {
        console.error("Error cargando evaluaciones del restaurante:", err);
      });
    } else {
      // Limpiar evaluaciones del restaurante cuando se cambia a host
      // O cuando desactivamos el fetch automático (porque el padre las provee)
      setHostEvaluations([]);
    }
  }, [selectedType, restoId, getEvalByResto, getEvalRestoById, disableRestoFetch]);

  // Cargar evaluaciones del camarero
  useEffect(() => {
    if (selectedType === "host" && selectedHostId) {
      const loadHostEvaluations = async () => {
        try {
          setHostLoading(true);
          setHostError(null);
          const res = await getEvaluationByHostId(selectedHostId);
          const evaluationsArray = Array.isArray(res) ? res : [];
          setHostEvaluations(evaluationsArray);


        } catch (err) {
          console.error("Error cargando evaluaciones del host:", err);
          setHostError(err.message || "Error al cargar evaluaciones del host");
          setHostEvaluations([]);
        } finally {
          setHostLoading(false);
        }
      };
      loadHostEvaluations();
    } else if (selectedType === "restaurant") {
      // Limpiar evaluaciones del host cuando se cambia a restaurante
      setHostEvaluations([]);
      setHostError(null);
    }
  }, [selectedType, selectedHostId]);

  // Determinar qué datos mostrar
  const isLoading = selectedType === "restaurant" ? loading : hostLoading;
  const hasError = selectedType === "restaurant" ? error : hostError;
  const evaluations = selectedType === "restaurant" ? restoEvaluation : hostEvaluations;
  const comparisonLabel = selectedType === "restaurant"
    ? "Promedio otros almacenes del mismo país"
    : "Promedio del resto del equipo";
  const entityName = selectedType === "restaurant" ? restoName : null;



  return (
    <Evaluations>
      <TableHeader>
        <span>#</span>
        <span>Categoría</span>
        <span>Valoración</span>
      </TableHeader>

      {hasError && (
        <ErrorMessage>
          ⚠️ {hasError}
        </ErrorMessage>
      )}

      {isLoading && (
        <>
          {[...Array(5)].map((_, idx) => (
            <SkeletonLoader key={idx} active={true} height="80px" margin="14px 0" borderRadius="14px" />
          ))}
        </>
      )}

      {!isLoading && !hasError && (!evaluations || evaluations.length === 0) && (
        <EmptyMessage>
          <EmptyMessageIcon src={boxIcon} alt="" aria-hidden="true" />
          <span>No hay evaluaciones disponibles</span>
        </EmptyMessage>
      )}

      {!isLoading && !hasError && (() => {
        // Filtrar y ordenar evaluaciones válidas
        const validEvaluations = (evaluations || [])
          .filter(item => {
            if (!item || !item._id) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ [UnifiedEvaluationsTable] Item inválido:', item);
              }
              return false;
            }
            // Filtrar solo preguntas válidas (question1-10)
            if (!item._id.match(/^question\d+$/)) {
              return false;
            }
            return true;
          })
          .sort((a, b) => {
            // Ordenar por número de pregunta (question1, question2, etc.)
            const numA = parseInt(a._id.replace('question', ''));
            const numB = parseInt(b._id.replace('question', ''));
            return numA - numB;
          })
          .slice(0, 5);

        if (process.env.NODE_ENV === 'development' && validEvaluations.length > 0) {
          console.log('🔍 [UnifiedEvaluationsTable] Evaluaciones válidas:', {
            total: validEvaluations.length,
            first: validEvaluations[0],
            all: validEvaluations.map(e => ({ _id: e._id, name: e.name, avg: e.avg, other: e.other }))
          });
        }

        return validEvaluations.map((item, idx) => {
          // Validar que avg y other sean números válidos
          const avgValue = typeof item.avg === 'number' ? item.avg : (parseFloat(item.avg) || 0);
          const otherValue = typeof item.other === 'number' ? item.other : (parseFloat(item.other) || 0);

          return (
            <EvaluationItem key={item._id || idx}>
              <EvaluationNumber>{idx + 1}</EvaluationNumber>
              <EvaluationContent>
                <Row>
                  <Left>
                    <Category>{getEvaluationShortLabel(item._id, item.name || "Sin nombre")}</Category>
                    {entityName && <Subinfo>{entityName}</Subinfo>}
                  </Left>
                  <Right>
                    <ScoreRuler value={avgValue} />
                  </Right>
                </Row>

                <Row>
                  <Left>
                    <Subinfo>{comparisonLabel}</Subinfo>
                  </Left>
                  <Right>
                    <ScoreRuler value={otherValue} />
                  </Right>
                </Row>
              </EvaluationContent>
            </EvaluationItem>
          );
        });
      })()}
    </Evaluations>
  );
}

// ================== Styled Components ==================

const Evaluations = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 20px auto;
  font-family: "Poppins", sans-serif;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 220px;
  font-weight: 500;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  color: #888;
  font-size: 14px;

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 450px) {
    display: none;
  }
`;

const EvaluationItem = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  margin: 14px 0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    position: relative;
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr;
    position: relative;
  }
`;

const EvaluationNumber = styled.div`
  font-weight: 600;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  @media (max-width: 768px) {
    position: absolute;
    top: 12px;
    left: 14px;
    min-width: auto;
    height: auto;
    padding: 0;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
  }

  @media (max-width: 450px) {
    position: absolute;
    top: 12px;
    left: 14px;
    min-width: auto;
    height: auto;
    padding: 0;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
  }
`;

const EvaluationContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  align-items: center;
  padding: 10px 16px;
  gap: 16px;

  &:first-child {
    border-bottom: 1px solid #eee;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 12px 14px;

    &:first-child {
      padding-top: 34px;
    }
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 12px 14px;
  }

  @media (max-width: 450px) {
    &:first-child {
      padding-top: 34px;
    }
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
`;

const Category = styled.div`
  font-weight: 600;
  font-size: 15px;
`;

const Subinfo = styled.div`
  font-size: 13px;
  color: #666;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 450px) {
    width: 100%;
  }
`;

const ScoreRulerWrap = styled.div`
  width: ${SCORE_TRACK_WIDTH}px;
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 450px) {
    width: 100%;
  }
`;

const ScoreValue = styled.div`
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 700;
  text-align: right;
  margin-bottom: 4px;
`;

const ScoreTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  margin-bottom: 4px;
`;

const ScoreTrackBase = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    #dc2626 0%,
    #dc2626 33.333%,
    #eab308 33.333%,
    #eab308 66.666%,
    #16a34a 66.666%,
    #16a34a 100%
  );
`;

const ScoreTrackOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => `${$width}%`};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.58);
`;

const ScaleTicks = styled.div`
  position: absolute;
  inset: 0;
`;

const ScaleTick = styled.span`
  position: absolute;
  top: -1px;
  left: ${({ $left }) => `${$left}%`};
  width: 1px;
  height: 12px;
  background: rgba(15, 23, 42, 0.3);
  transform: translateX(-50%);
`;

const ScoreMarker = styled.span`
  position: absolute;
  top: 50%;
  left: ${({ $left }) => `${$left}%`};
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid ${({ $color }) => $color};
  background: #fff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.18);
  transform: translate(-50%, -50%);
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #64748b;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  margin: 20px 0;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  text-align: center;
`;

const EmptyMessage = styled.div`
  display: grid;
  justify-items: center;
  gap: 12px;
  padding: 40px 16px;
  text-align: center;
  color: #888;
  font-size: 14px;
`;

const EmptyMessageIcon = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
`;
